// Header navigation for the app. Includes a sign out button, theme toggle,
// and the top-level page links.
//
// Built on react-bootstrap's <Navbar> so we get a real mobile hamburger
// (Navbar.Toggle + Navbar.Collapse) and aria-current routing for free via
// react-router's <NavLink>.

import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Navbar from "react-bootstrap/Navbar";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import { Spinner } from "./Spinner";
import ThemeToggle from "./ThemeToggle";
import * as Urls from '../fleet-shared/Urls.mjs';
import { PRODUCT_NAME } from '../fleet-shared/Brand.mjs';

// The production site (www.taskpogo.com) uses the prod app icon; everywhere
// else — local dev and dev.taskpogo.com — uses the dev icon so it's obvious
// at a glance which environment you're looking at.
const isProdHost =
  typeof window !== "undefined" && window.location.hostname === "www.taskpogo.com";
const appIcon = isProdHost ? "/logo192.png" : "/logo192-dev.png";

const SignedInMenu = ({ signOut }) => (
  <Dropdown align="end">
    <Dropdown.Toggle
      variant="primary"
      id="dropdownMenuButton"
      aria-label="Account menu"
    >
      <i className="fa-solid fa-user" aria-hidden="true"></i>
    </Dropdown.Toggle>
    <Dropdown.Menu>
      <Dropdown.Item as={Link} to={Urls.urlForProfile()}>Profile</Dropdown.Item>
      <Dropdown.Item as={Link} to={Urls.urlForChangePassword()}>Change Password</Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item
        as="button"
        id="signOut"
        onClick={() => signOut()}
        className="text-danger"
      >
        Sign Out
      </Dropdown.Item>
    </Dropdown.Menu>
  </Dropdown>
);

const SignedOutMenu = () => {
  const navigate = useNavigate();
  return (
    <Button variant="primary" id="signIn" onClick={() => navigate(Urls.urlForAllItems())}>
      <i className="fa-solid fa-user me-2" aria-hidden="true"></i>
      Sign In/Up
    </Button>
  );
};

const ProfileSlot = ({ user, isInitializing, signOut }) => {
  if (isInitializing) return <Spinner />;
  if (user != null) return <SignedInMenu signOut={signOut} />;
  return <SignedOutMenu />;
};

// Use react-router's NavLink with react-bootstrap's Nav.Link so we get
// aria-current="page" and an "active" class on the matching route.
const NavItem = ({ to, end, children }) => (
  <Nav.Link as={NavLink} to={to} end={end} className="nav-item-link">
    {children}
  </Nav.Link>
);

function Navigation({ user, isInitializing, signOut }) {
  return (
    <Navbar
      expand="md"
      sticky="top"
      bg="body"
      data-bs-theme="auto"
      className="border-bottom shadow-sm"
    >
      <Container>
        <Navbar.Brand as={Link} to="/">
          <img
            src={appIcon}
            width="28"
            height="28"
            alt=""
            className="d-inline-block align-text-bottom me-2"
          />
          {PRODUCT_NAME}
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="main-nav" />

        <Navbar.Collapse id="main-nav">
          <Nav className="me-auto">
            {/*
              Trips leads, ahead of Items: trip planning is what the product
              is for now, and the nav is the shortest statement of that.

              "Weather" rather than "Salish Sea". The route stays /salish --
              it is in sitemap.xml, in Urls.urlForSalish, and in whatever
              inbound links exist -- and the PAGE keeps its own honest heading
              ("Salish Sea marine weather", with the Puget Sound to Desolation
              Sound scope in the line below it). So the nav names the feature
              and the page discloses its scope above the fold, which is the
              trade a Nav.Link with no sub-label slot leaves available.
            */}
            <NavItem to="/" end>Home</NavItem>
            <NavItem to={Urls.urlForAllTrips()}>Trips</NavItem>
            <NavItem to={Urls.urlForAllItems()}>Items</NavItem>
            <NavItem to={Urls.urlForSalish()}>Weather</NavItem>
            <NavItem to={Urls.urlForSupport()}>Support</NavItem>
          </Nav>

          <Nav className="align-items-center gap-1">
            <ThemeToggle />
            <ProfileSlot user={user} isInitializing={isInitializing} signOut={signOut} />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default Navigation;
